import { Button, Flex, Group, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import MainTable from "../../../../../layouts/Table/MainTable";
import ItemDeleter from "../../../../../components/TableComponents/ItemDeleter";
import { useContactHandler } from "../../components/useContactHandler";
import { IconArrowBarUp } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import TableFilterExport from "../../../../../components/TableComponents/TableFilterExport";
import { useProfileStore } from "../../../../../stores/UserStore";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import {
  deleteUnsub,
  downloadUnsubscribers,
  getFirstPageUnsub,
  getFirstPageUnsubAdmin,
  getUnsubInOrg,
  searchAllUnsub,
  searchAllUnsubAdmin,
} from "../../../../../api/UnsubscribersAPI";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";

function resultsHandler(results) {
  return results.map((data) => ({
    ...data,
    id: data.id,
    [data.value_type]: data.value,
    organization_name: data?.organization_name,
    organization_id: data.organization_id,
    created_by: data.created_by,
    value_type: data.value_type
  }));
}

const allColumnFields = ["email", "domain_email", "linkedin_url", "company", "phone", "siren", "siret", "naf_code"];

function columnsHandler(isAll) {
  const lang = useLanguage.getState().language;
  return [
    numberColumn(),
    {
      field: "organization_name",
      headerName: lang.unsubscribers.columns.org,
      width: 120,
      renderCell: ({ row, value }) => {
        return <Text>{row.organization_name}</Text>;
      },
      id: "organization_id",
      sortable: false,
      getFilterOptions: () => {
        const profile = useProfileStore.getState().profile;
        return profile.organizations.map((x) => ({
          label: x.name,
          value: x.id.toString(),
        }));
      },
      disabledCondition: true,
    },
    dateColumn(),
    {
      field: "email",
      headerName: lang.unsubscribers.columns.email,
      width: 150,
      filterable: false,
      sortable: false,
      id: "email",
    },
    {
      field: "domain_email",
      headerName: lang.unsubscribers.columns.domain_email,
      width: 150,
      filterable: false,
      sortable: false,
      id: "domain_email",
    },
    {
      field: "linkedin_url",
      headerName: lang.unsubscribers.columns.linkedin_url,
      width: 150,
      filterable: false,
      sortable: false,
      id: "linkedin_url",
    },
    {
      field: "company",
      headerName: lang.unsubscribers.columns.company,
      width: 150,
      filterable: false,
      sortable: false,
      id: "company",
    },
    {
      field: "phone",
      headerName: lang.unsubscribers.columns.phone,
      width: 150,
      filterable: false,
      sortable: false,
      id: "phone",
    },
    {
      field: "siren",
      headerName: lang.unsubscribers.columns.siren,
      width: 150,
      filterable: false,
      sortable: false,
      id: "siren",
    },
    {
      field: "siret",
      headerName: lang.unsubscribers.columns.siret,
      width: 150,
      filterable: false,
      sortable: false,
      id: "siret",
    },
    {
      field: "naf_code",
      headerName: lang.unsubscribers.columns.naf_code,
      width: 150,
      filterable: false,
      sortable: false,
      id: "naf_code",
    },
    {
      field: "value_type",
      headerName: "Type",
      width: 150,
      filterable: true,
      sortable: false,
      id: "value_type",
      type: "singleSelect",
      disabledCondition: true,
      valueOptions: allColumnFields.map((col) => ({
        label: lang.unsubscribers.columns[col] || col,
        value: col,
      })),
    },
  ];
}

const UnsubsContent = ({ listId }) => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();

  const startingColumns = useMemo(() => [numberColumn(), dateColumn()], []);
  const {
    loading,
    columns,
    rows,
    rowCount,
    handleLoading,
    onFilterChange,
    onPaginationChange,
    onSortChange,
  } = useContactHandler({
    apiRef,
    listId: parseInt(listId) || null,
    getFirstPage:
      listId === "admin"
        ? getFirstPageUnsubAdmin
        : listId === "all"
          ? getFirstPageUnsub
          : getUnsubInOrg,
    searchFunction: listId === "admin" ? searchAllUnsubAdmin : searchAllUnsub,
    resultsHandler,
    columnsHandler,
    disableQueryTransform: true,
    startingColumns,
    additionalFields: { value_type: null },
  });

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <ItemDeleter
          title_bold={`#${row.id} ?`}
          handleDelete={deleteUnsub}
          deletedMessage={lang.unsubscribers.delete_success}
          id={row.id}
          apiRef={apiRef}
        />
      );
    },
    [lang.unsubscribers.delete_success, apiRef]
  );

  const toolbarActions = useCallback(
    ({ selectedIds, filter }) => (
      <>
        <TableFilterExport
          selectedIds={selectedIds}
          filter={filter}
          handleExport={downloadUnsubscribers}
          name={"Unsubscribers"}
          filterResultNumber={rowCount}
          total={rowCount}
          withExcludedIds={false}
          withSelection={false}
        />
        <SelectionDeleter
          handleDelete={deleteUnsub}
          apiRef={apiRef}
          setTableLoading={() => handleLoading()}
          ids={selectedIds}
        />
      </>
    ),
    [apiRef, handleLoading, rowCount]
  );

  useLayoutEffect(() => {
    if (listId && apiRef.current?.state) {
      handleLoading();
    }
  }, [listId, apiRef]);

  if (!columns) {
    return;
  }

  return (
    <Stack h={"100%"} flex={1} pr={16} gap={8} pt={12} miw={950}>
      <Group mt={4}>
        <Text fw={700} fz={24}>
          {lang.prospects.unsub_contacts}
        </Text>

        {listId !== "all" && listId !== "admin" && (
          <Button
            leftSection={<IconArrowBarUp size={24} fill="#fff" stroke={0} />}
            fz={16}
            ml={"auto"}
            radius={"md"}
            size="md"
            className="main-list-action"
            px={24}
            component={Link}
            variant="outline"
            to={`/unsubscribers/${listId}/import`}
          >
            {lang.prospects.import_in_org}
          </Button>
        )}
      </Group>

      <Flex h={0} flex={1} mt={10}>
        <MainTable
          isClient={false}
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          setLoading={() => {
            handleLoading();
          }}
          id={"list-table"}
          hasFolder
          // pageName={"contact_lists_contacts"}
          onPaginationChange={onPaginationChange}
          actionsCell={actionsCell}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          // pageOrderName="contact_list_fetch_contacts_order"
          actionsCellWidth={50}
          withBorderTop
          whiteButtons
          toolbarActions={toolbarActions}
          tableSx={{
            overflow: "hidden",
            "& .MuiDataGrid-pinnedColumns": {
              background: "#fff",
              borderTopLeftRadius: 3,
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "#fff",
            },
            "& .table-search": {
              width: "300px !important",
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default UnsubsContent;
